import { colors } from '@iq/react-components';
import config from './config';

export const MODE_SUMMARY = 'mode/summary';

export const NO_SITES_MESSAGE = 'No sites to display';

/*
 * @react-components colors
 * $StatusBlue: [51, 102, 255]
 * $StatusGreen: [12, 169, 25]
 * $StatusOrange: [255, 115, 0]
 * $StatusRed: [240, 48, 64]
 */

export const STATUSES = {
  default: {
    icon: 'empty',
    color: [51, 102, 255],
    colorHex: 0xff_73_00,
  },
  0: {
    icon: 'he-statushealthy',
    color: [12, 169, 25],
    colorHex: 0x18_bc_19,
  },
  1: {
    icon: 'he-statuswarning',
    color: [255, 115, 0],
    colorHex: 0xff_73_00,
  },
  2: {
    icon: 'he-statusrisk',
    color: [240, 48, 64],
    colorHex: 0xf0_30_40,
  },
  r1: {
    icon: 'he-statusrisk',
    text: 'Low Risk',
    color: [102, 102, 102],
    colorHex: 0x66_66_66,
  },
  r2: {
    icon: 'he-statusrisk',
    text: 'Medium Risk',
    color: [249, 200, 70],
    colorHex: 0xf9_c8_46,
  },
  r3: {
    icon: 'he-statusrisk',
    text: 'High Risk',
    color: [237, 71, 71],
    colorHex: 0xed_47_47,
  },
};

export const RISK_SEVERITY_ORDER = ['r3', 'r2', 'r1'];

export const SEVERITIES = {
  critical: 'Emergency',
  warning: 'Warning / Severe',
  minor: 'Minor',
  info: 'Normal',
  internal: 'Maintenance / Internal',
};

// ==============================================
// Alarm Constants
// ==============================================

// Alarm list pagination and display
export const ALARM_CONTAINER_HEIGHT = 600;
export const ALARM_ROW_HEIGHT = 31;
export const ALARM_HEADER_HEIGHT = 37; // Matches $row-height: 2.3rem in SCSS
export const ALARM_API_BATCH_SIZE = 50; // Smaller batches for faster sorting

// Alarm severity colors (using @react-components colors)
// Normal: Green - $StatusGreen: [12, 169, 25] -> #0CA919
// Warning: Yellow - $StatusYellow: [255, 200, 0] -> #FFC800
// Alert: Red - $StatusRed: [240, 48, 64] -> #F03040
// Unknown: Grey
export const ALARM_SEVERITY_COLORS = {
  normal: 'rgb(12, 169, 25)',
  warning: 'rgb(255, 200, 0)',
  alert: 'rgb(240, 48, 64)',
  unknown: 'rgb(128, 128, 128)',
};

// ==============================================
// DTMP Severities
// ==============================================
// value is the display name shown to users
// Based on backend EVENT_SEVERITY_MAPPING: Normal→info, Warning→warning, Alert→critical, Error→error
export const DTMP_SEVERITIES = {
  info: 'Normal',
  warning: 'Warning',
  critical: 'Alert',
};

export const THRESHOLD_TYPES = {
  linear: {
    key: 'linearItems',
    name: 'Linear',
    value: 'linear',
  },
  area: {
    key: 'areaItems',
    name: 'Area',
    value: 'area',
  },
};

export const PAGE_TYPES = {
  stateset: 'Stateset',
  risk: 'Risk',
  health: 'Health',
  none: 'None',
};
export const DEFAULT_PAGETYPE = 'none';

export const DEFAULT_ON_PREMISE_AGGREGATION_OPTIONS = [
  {
    label: 'Average',
    value: 'avg',
  },
  {
    label: 'Min',
    value: 'min',
  },
  {
    label: 'Max',
    value: 'max',
  },
];

export const DEFAULT_AGGREGATION_OPTIONS = [
  ...DEFAULT_ON_PREMISE_AGGREGATION_OPTIONS,
  {
    label: 'First',
    value: 'first',
  },
  {
    label: 'Last',
    value: 'last',
  },
];

const ALL_GRANULARITY_OPTIONS = [
  {
    label: 'Second',
    value: '1s',
  },
  {
    label: 'Minute',
    value: '60s',
  },
  {
    label: 'Fifteen minutes',
    value: '900s',
  },
  {
    label: 'Thirty minutes',
    value: '1800s',
  },
  {
    label: 'Hour',
    value: '3600s',
  },
  {
    label: 'Six hours',
    value: '21600s',
  },
  {
    label: 'Day',
    value: '86400s',
  },
  {
    label: 'Week',
    value: '604800s',
  },
  {
    label: 'Month',
    value: '2592000s',
  },
  {
    label: 'Quarter',
    value: '7862400s',
  },
  {
    label: 'Year',
    value: '31536000s',
  },
];
const ON_PREM_SPECIFIC_GRANULARITIES = new Set(['Six hours']);
const DEC_SPECIFIC_GRANULARITIES = new Set(['Second', 'Thirty minutes', 'Quarter', 'Year']);
const GRANULARITY_OPTIONS_ON_PREMISE = ALL_GRANULARITY_OPTIONS.filter(
  (option) => !ON_PREM_SPECIFIC_GRANULARITIES.has(option.label)
);
const GRANULARITY_OPTIONS_DEC = ALL_GRANULARITY_OPTIONS.filter(
  (option) => !DEC_SPECIFIC_GRANULARITIES.has(option.label)
);
export const GRANULARITY_OPTIONS = config.onPremise
  ? GRANULARITY_OPTIONS_ON_PREMISE
  : GRANULARITY_OPTIONS_DEC;

export const EXPORT_TIMESERIES_PROGRESS_LOGIC_EC = {
  // This is the progress prediction logic for EC adapter
  // noOfDatapoints: {noOfSignals: [ProgressIncrement,timeInterval]}
  10_000: {
    10: [6, 300],
    50: [5, 500],
    100: [4, 800],
    150: [3, 1100],
    200: [3, 1400],
    default: [6, 1000],
  },
  50_000: {
    10: [3, 300],
    50: [2, 500],
    100: [2, 800],
    150: [2, 1100],
    200: [2, 1400],
    default: [3, 1000],
  },
  100_000: {
    10: [3, 300],
    50: [2, 500],
    100: [2, 800],
    150: [2, 1100],
    200: [2, 1400],
    default: [3, 1000],
  },
};

export const EXPORT_TIMESERIES_DATA_POINTS_LIMIT_EC = 10_000;
export const EXPORT_TIMESERIES_PANEL = 'timeseriesExportPanel';
export const SIGNAL_VIEWER_DATETIME = 'signalviewerdatetime';

export const EXPORT_EVENT_PANEL = 'eventExportPanel';
export const EXPORT_EVENT_LIMIT = 300_000;

export const EVENT_TYPE_USER = 'user';
export const EVENT_TYPE_DTMP = 'dtmpEvent';
export const EVENT_TYPE_MACH = 'machEvent';

export const USER_EVENTS = [
  {
    id: 'pending',
    type: 'Pending',
    color: '#B3B3B3',
    border: '#666666',
  },
  {
    id: 'todo',
    type: 'To-Do',
    color: '#EDE27F',
    border: '#BEB34F',
  },
  {
    id: 'open',
    type: 'Open',
    color: '#63C0E5',
    border: '#357D9C',
  },
  {
    id: 'overdue',
    type: 'Overdue',
    color: '#ec575b',
    border: '#980C21',
  },
  {
    id: 'closed',
    type: 'Closed',
    color: '#2F9C2D',
    border: '#196E17',
  },
  {
    id: 'stateless',
    type: 'N/A',
    color: '#FFFFFF',
    border: '#666666',
  },
];

export const EVENT_DEFAULT_MARGIN = {
  top: 25,
  right: 0,
  bottom: 25,
  left: 70,
};

export const FORM_STATES = {
  view: 'view',
  edit: 'edit',
  editLimited: 'edit-limited',
  create: 'create',
};

export const LIMITED_PERMISSION_ENTITIES = { events: ['Write'] }; // Permission types will be changed to array when we redesign UI to support multiple limited permissions

export const LIMITED_PERMISSIONS = Object.entries(LIMITED_PERMISSION_ENTITIES).reduce(
  (permissionList, [entity, limitablePermissions]) => {
    for (const permission of limitablePermissions) permissionList.push(`${entity}/${permission}`);
    return permissionList;
  },
  []
);

export const USER_EVENT_STATUSES = ['All', 'Pending', 'Open', 'Closed', 'Overdue', 'ToDo'];

export const USER_EVENT_ACTIVITY = ['All', 'Planned', 'Reactive'];

export const ENITIY_ACTION_MAP = { 0: 'Limited', 1: 'Read', 2: 'Write', 3: 'Delete' };

export const STATELESS_EVENTS = ['system-trip', 'unplanned-outage', 'general'];

export const IOT_EVENT_SOURCE_TYPES = ['elasticsearch'];

export const INTERNAL_DATA_SOURCE = 'identiq';

export const SELECTABLE_VIZUALISATIONS = ['graph', 'duval', 'star', 'duvalPentagon'];

export const TAG_CATEGORIES = ['files', 'events', 'visualizations'];

// Map known device types to a friendlier display label.
// Type strings come with a version suffix (e.g. "@1"). Match by the
// versionless prefix so future versions still map correctly.
export const TYPE_LABEL_OVERRIDES = {
  'Core.Edge.Edge': 'Edge',
  'PG.MIMS.Equipment.MimsComputer': 'MIMS Computer',
  'PG.MIMS.Equipment.MimsService': 'MIMS Service',
  'PG.MIMS.Equipment.MainComputer': 'Main Computer',
  'PG.MIMS.Equipment.ScmComputer': 'SCM',
  'PG.MIMS.Equipment.MsmComputer': 'MSM',
  'PG.MIMS.Equipment.NasComputer': 'Storage',
};

// Display order for device groups (matches the Heartbeat & Status Monitoring spec).
export const TYPE_ORDER = [
  'Core.Edge.Edge',
  'PG.MIMS.Equipment.MimsComputer',
  'PG.MIMS.Equipment.MimsService',
  'PG.MIMS.Equipment.MainComputer',
  'PG.MIMS.Equipment.ScmComputer',
  'PG.MIMS.Equipment.MsmComputer',
  'PG.MIMS.Equipment.NasComputer',
];

export const DEVICE_STATUS_COLOR = {
  0: colors.StatusRed,
  1: colors.StatusGreen,
  2: colors.Grey50,
};

export const STATUS_CLASS = {
  0: 'offline',
  1: 'online',
  2: 'unknown',
};

export const STATUS_LABEL = {
  0: 'Disconnected',
  1: 'Connected',
  2: 'Unknown',
};
